import ChartDataTable from '@/pages/CompanyReport/components/Overview/components/ChartDataTable';
import DocumentSchema from '@/components/DocumentSchema';
import type { Table } from 'schema-dts';
import { useSelector, shallowEqual } from 'react-redux';
import { selector } from './selector';
import Tooltip from '@/components/Tooltip';
import {
  Term,
  TooltipTitle,
} from '@/pages/CompanyReport/components/Overview/components/Fundamentals/styled';
import {
  OverviewTranslation,
  useOverviewTranslation,
} from '@/pages/CompanyReport/components/OverviewSection/hooks/useOverviewTranslation';
import type { ReactNode } from 'react';

const TTMTooltip = ({ children }: { children: ReactNode }) => {
  const t = useOverviewTranslation();
  return (
    <Tooltip
      title={
        <TooltipTitle>{t('fundamentals.income.table.tooltip')}</TooltipTitle>
      }
      light
      openDelay={300}
    >
      <Term>{children}</Term>
    </Tooltip>
  );
};

const IncomeTable = () => {
  const selected = useSelector<
    Parameters<typeof selector>[0],
    ReturnType<typeof selector>
  >(selector, shallowEqual);

  const t = useOverviewTranslation();

  return (
    <>
      <DocumentSchema<Table>
        item={{
          '@context': 'https://schema.org',
          '@type': 'Table',
          headline: t('fundamentals.income.table.heading', {
            tickerSymbol: selected?.tickerSymbol,
          }),
          about: t('fundamentals.income.table.description'),
        }}
      />
      <ChartDataTable
        data-cy-id="overview-income-table"
        columnTextAlignments="l"
      >
        <caption>{t('fundamentals.income.table.description')}</caption>
        <thead>
          <tr>
            <th colSpan={2}>
              <OverviewTranslation
                i18nKey="fundamentals.income.table.header"
                values={{ tickerSymbol: selected?.tickerSymbol }}
              >
                {selected?.tickerSymbol} income statement (
                <TTMTooltip>TTM</TTMTooltip>)
              </OverviewTranslation>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{t('fundamentals.income.table.revenue')}</td>
            <td>{selected?.revenueFormattedFull}</td>
          </tr>
          <tr>
            <td>{t('fundamentals.income.table.costOfRevenue')}</td>
            <td>{selected?.costOfRevenueFormattedFull}</td>
          </tr>
          <tr>
            <td>{t('fundamentals.income.table.grossProfit')}</td>
            <td>{selected?.grossProfitFormattedFull}</td>
          </tr>
          <tr>
            <td>{t('fundamentals.income.table.other')}</td>
            <td>{selected?.expensesFormattedFull}</td>
          </tr>
          <tr>
            <td>{t('fundamentals.income.table.earnings')}</td>
            <td>{selected?.earningsFormattedFull}</td>
          </tr>
        </tbody>
      </ChartDataTable>
    </>
  );
};

export default IncomeTable;
